import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { Form, message } from "antd";
import {
  fetchChargesByAggId,
  clearCharges,
  handleCheckedChargesNull,
} from "../../store/slices/charges";
import AccountCharges from "../../components/AccountCharges/AccountCharges";
import { toHttpFormatEndOfDay, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import { fetchServiceTypes } from "../../store/slices/services";
import {
  fetchRateModes,
  fetchRateType,
  fetchServiceMode,
  fetchServiceStatus,
} from "../../store/slices/rtkDictionaries";
import { fetchGetAgreementsByAccId } from "../../store/slices/agreements";
import { getChargesRoute } from "../../lib/routes/charging";

const AccountChargesContainer = () => {
  const chargesData = useSelector((state) => state.charges.charges);
  const { checkedChargesNull } = useSelector((state) => state.charges);
  const [data, setData] = useState("");
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const dispatch = useDispatch();
  const { accountId } = useParams();
  const sessionStorageSearchParams = JSON.parse(sessionStorage.getItem("chargeFilters"))
    ?.chargeParams;

  // Очистка списка начислений по выходу
  useEffect(
    () => () => {
      dispatch(clearCharges());
    },
    []
  );

  const chargesSearchParams = useMemo(() => {
    const nowDate = moment();
    const firstDayOfCurrentMonth = moment().startOf("month");
    const firstDayOfPreviousMonth = moment().subtract(1, "months").startOf("month");
    const lastDayOfPreviousMonth = moment().subtract(1, "months").endOf("month");
    return {
      accountId,
      agreementId: sessionStorageSearchParams ? sessionStorageSearchParams?.agreementId : null,
      page: sessionStorageSearchParams ? sessionStorageSearchParams?.page : 0,
      size: sessionStorageSearchParams ? sessionStorageSearchParams?.size : 20,
      rateTypeId: sessionStorageSearchParams ? sessionStorageSearchParams?.rateTypeId : null,
      clientFullName: sessionStorageSearchParams ? sessionStorageSearchParams?.clientFullName : "",
      serviceModeId: sessionStorageSearchParams ? sessionStorageSearchParams?.serviceModeId : null,
      serviceTypeName: sessionStorageSearchParams
        ? sessionStorageSearchParams?.serviceTypeName
        : null,
      unitsFrom: sessionStorageSearchParams ? sessionStorageSearchParams?.unitsFrom : null,
      unitsTo: sessionStorageSearchParams ? sessionStorageSearchParams?.unitsTo : null,
      chargeAmountFrom: sessionStorageSearchParams
        ? sessionStorageSearchParams?.chargeAmountFrom
        : null,
      chargeAmountTo: sessionStorageSearchParams
        ? sessionStorageSearchParams?.chargeAmountTo
        : null,
      chargeDate: [
        sessionStorageSearchParams
          ? moment(sessionStorageSearchParams?.chargeDateFrom)
          : firstDayOfCurrentMonth,
        sessionStorageSearchParams ? moment(sessionStorageSearchParams?.chargeDateTo) : nowDate,
      ],
      chargePeriod: [
        sessionStorageSearchParams
          ? moment(sessionStorageSearchParams?.chargePeriodFrom)
          : firstDayOfPreviousMonth,
        sessionStorageSearchParams
          ? moment(sessionStorageSearchParams?.chargePeriodTo)
          : lastDayOfPreviousMonth,
      ],
      chargeDateFrom: sessionStorageSearchParams ? sessionStorageSearchParams?.chargeDateFrom : "",
      chargeDateTo: sessionStorageSearchParams ? sessionStorageSearchParams?.chargeDateTo : "",
      chargePeriodFrom: sessionStorageSearchParams
        ? sessionStorageSearchParams?.chargePeriodFrom
        : firstDayOfPreviousMonth,
      chargePeriodTo: sessionStorageSearchParams
        ? sessionStorageSearchParams?.chargePeriodTo
        : lastDayOfPreviousMonth,
      invoiceExtNum: sessionStorageSearchParams ? sessionStorageSearchParams?.invoiceExtNum : "",
      invoiceDateFrom: sessionStorageSearchParams
        ? sessionStorageSearchParams?.invoiceDateFrom
        : "",
      invoiceDateTo: sessionStorageSearchParams ? sessionStorageSearchParams?.invoiceDateTo : "",
      billNumber: sessionStorageSearchParams ? sessionStorageSearchParams?.billNumber : null,
      billAmountFrom: sessionStorageSearchParams
        ? sessionStorageSearchParams?.billAmountFrom
        : null,
      billAmountTo: sessionStorageSearchParams ? sessionStorageSearchParams?.billAmountTo : null,
      billDateFrom: sessionStorageSearchParams ? sessionStorageSearchParams?.billDateFrom : "",
      billDateTo: sessionStorageSearchParams ? sessionStorageSearchParams?.billDateTo : "",
      billPeriodFrom: sessionStorageSearchParams ? sessionStorageSearchParams?.billPeriodFrom : "",
      billPeriodTo: sessionStorageSearchParams ? sessionStorageSearchParams?.billPeriodTo : "",
      isAdjustment: sessionStorageSearchParams ? sessionStorageSearchParams?.isAdjustment : null,
      invoiceId: sessionStorageSearchParams ? sessionStorageSearchParams?.invoiceId : undefined,
      rateModeId: sessionStorageSearchParams ? sessionStorageSearchParams?.rateModeId : undefined,
      billDate: sessionStorageSearchParams ? sessionStorageSearchParams?.billDate : undefined,
      billPeriod: sessionStorageSearchParams ? sessionStorageSearchParams?.billPeriod : undefined,
      chargeIds: sessionStorageSearchParams ? sessionStorageSearchParams?.chargeIds : undefined,
      invoiceDate: sessionStorageSearchParams ? sessionStorageSearchParams?.invoiceDate : undefined,
    };
  }, [accountId, sessionStorageSearchParams]);

  const initialParamsState = {
    ...chargesSearchParams,
    chargeDateFrom: toHttpFormatStartOfDay(chargesSearchParams.chargeDate[0]),
    chargeDateTo: toHttpFormatEndOfDay(chargesSearchParams.chargeDate[1]),
    chargePeriodFrom: toHttpFormatStartOfDay(chargesSearchParams.chargePeriod[0]),
    chargePeriodTo: toHttpFormatEndOfDay(chargesSearchParams.chargePeriod[1]),
    chargeDate: "",
    chargePeriod: "",
  };

  const [chargeParams, setChargeParams] = useState(initialParamsState);

  const displayError = () => {
    message.error({
      content: "Ошибка загрузки начислений. Попробуйте позже",
      style: {
        marginTop: "15vh",
      },
    });
  };

  const fetchChargesAll = () => {
    const url = getChargesRoute({ ...chargeParams, size: 1 });
    return axios(url)
      .then(({ data }) => {
        const { totalElements } = data; // получение общего числа начислений
        if (totalElements) {
          const url2 = getChargesRoute({ ...chargeParams, size: totalElements });
          return axios(url2) // получение всех начислений
            .then(({ data }) => data.content)
            .catch((e) => {
              displayError();
              throw new Error(e);
            });
        }
        return null;
      })
      .catch((e) => {
        displayError();
        throw new Error(e);
      });
  };

  const fetchCharges = (values) => {
    dispatch(fetchChargesByAggId(values)).then(() => {
      sessionStorage.removeItem("chargeFilters");
    });
  };

  useEffect(() => {
    if (accountId) dispatch(fetchGetAgreementsByAccId({ accId: accountId }));
  }, [accountId]);

  useEffect(() => {
    const filteredZeroCharges =
      Array.isArray(chargesData) && checkedChargesNull
        ? chargesData?.filter((item) => item.chargeAmount !== 0)
        : chargesData;
    setData(filteredZeroCharges);
  }, [chargesData]);

  useEffect(() => {
    dispatch(fetchServiceStatus());
    dispatch(fetchServiceMode());
    dispatch(fetchServiceTypes());
    dispatch(fetchRateType());
    dispatch(fetchRateModes());
  }, []);

  const toggleZeroCharges = (e) => {
    dispatch(handleCheckedChargesNull(e.target.checked));
    // const filteredZeroCharges = chargesData?.content.filter((item) => item.chargeAmount !== 0);
    const filteredZeroCharges = chargesData.filter((item) => item.chargeAmount !== 0);
    // setData(e.target.checked ? filteredZeroCharges : chargesData?.content);
    setData(e.target.checked ? filteredZeroCharges : chargesData);
  };

  const handlePageSize = (field, value) => {
    switch (field) {
      case "size":
        setChargeParams({ ...chargeParams, size: Number(value), page: 0 });
        fetchCharges({ ...chargeParams, size: Number(value), page: 0 });
        break;
      case "page":
        setChargeParams({ ...chargeParams, page: Number(value) - 1 });
        fetchCharges({ ...chargeParams, page: Number(value) - 1 });
        setSelectedRowKeys([]);
        break;
      default:
        break;
    }
  };

  const handleSearchCharges = (values) => {
    setChargeParams(values);
    fetchCharges(values);
  };

  const getValueChargesPagingAndFilters = (field) => chargeParams[field];

  const resetSearchCharges = useCallback(() => {
    sessionStorage.removeItem("chargeFilters");
    setTimeout(() => {
      form.resetFields();
    }, 100);
    setChargeParams(initialParamsState);
    dispatch(clearCharges());
  }, [initialParamsState]);

  return (
    <>
      <AccountCharges
        data={data}
        checked={checkedChargesNull}
        chargesData={chargesData}
        accountId={accountId}
        chargesSearchParams={chargesSearchParams}
        getValueChargesPagingAndFilters={getValueChargesPagingAndFilters}
        resetSearchCharges={resetSearchCharges}
        handleSearchCharges={handleSearchCharges}
        toggleZeroCharges={toggleZeroCharges}
        fetchChargesAll={fetchChargesAll}
        handlePageSize={handlePageSize}
        formFilters={form}
        chargeParams={chargeParams}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </>
  );
};

export default AccountChargesContainer;
